import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import List, { ListItem, ListItemProps } from "../List";
import smoothJoin from "../smoothJoin";
import { useFieldId, useFieldValidity } from "./utilities";
import FieldProps from "./FieldProps";
import FieldDropdown from "./components/FieldDropdown";
import FieldInput from "./components/FieldInput";
import FieldWithButtons from "./components/FieldWithButtons";
import FieldWrapper from "./components/FieldWrapper";
import Divider from "@hpo/client/components/Divider";
import Button from "@hpo/client/components/Button";

type MultiSelectFieldProps<D> = FieldProps<Array<string>> & {
  options: Array<D>;
  renderOption: (item: D) => ListItemProps;
  keyExtractor: (d: D) => string;
  join?: null | ((options: Array<D>) => string);
};

export default function MultiSelectField<T>(props: MultiSelectFieldProps<T>) {
  const {
    value,
    onChange,
    required = false,
    errorMessage,
    keyExtractor,
    options,
    renderOption,
    join = (options) =>
      smoothJoin(
        options.map(renderOption).map((o) => o.label),
        { lastJoiner: " et " },
      ),
  } = props;

  const id = useFieldId(props.id);

  // Remove selected options out of the options list
  useEffect(() => {
    if (!onChange) return;
    const optionsKeys = options.map(keyExtractor);
    const inOptionValues = value.filter((v) => optionsKeys.includes(v));
    if (inOptionValues.join(",") === value.join(",")) return;
    onChange(inOptionValues);
  }, [value.join(" ")]);

  const selectedOptions = useMemo(() => {
    const selectedOptions: Array<T> = [];
    value.forEach((v) => {
      const found = options.find((o) => keyExtractor(o) === v);
      if (found) selectedOptions.push(found);
    });
    return selectedOptions;
  }, [value.join(" "), options]);

  const validity = useFieldValidity(
    value,
    required,
    (v) => v === undefined || v === null || v.length === 0,
    (v) => {
      if (errorMessage) return errorMessage(v);
      return null;
    },
  );

  const onToggle = useCallback(
    (v: string) => {
      if (onChange) {
        const isIncluded = value.reduce(
          (acc, elem) => elem === v || acc,
          false,
        );
        if (isIncluded) {
          const newValue = value.filter((elem) => elem !== v);
          onChange(newValue);
        } else onChange([...value, v]);
      }
    },
    [onChange, value],
  );

  const [dropdown, setDropdown] = useState<boolean>(false);

  let inputNode: ReactNode;

  const [finalized, setFinalized] = useState(false);

  if (join !== null) {
    inputNode = (
      <FieldInput
        value={join(selectedOptions)}
        placeholder={props.placeholder}
        readOnly
        onFocus={() => setDropdown(true)}
      />
    );
  } else {
    inputNode = (
      <Fragment>
        <List
          noFrame
          data={selectedOptions}
          renderItem={(i) => <ListItem {...renderOption(i)} />}
        />
        <Divider vertical />
      </Fragment>
    );
  }

  inputNode = (
    <FieldWithButtons
      buttons={
        <Fragment>
          {value !== null && onChange ? (
            <Button
              icon={{ name: "close" }}
              style="discreet"
              onClick={() => onChange([])}
            />
          ) : null}
          <Button
            icon={{ name: "chevron", rotate: dropdown ? 0 : 180 }}
            style="discreet"
            onClick={() => setDropdown(!dropdown)}
          />
        </Fragment>
      }
    >
      {inputNode}
    </FieldWithButtons>
  );

  return (
    <FieldWrapper {...props} id={id} validity={validity} finalized={finalized}>
      {inputNode}
      <FieldDropdown
        visible={dropdown}
        onHide={() => {
          setDropdown(false);
          setFinalized(true);
        }}
      >
        <List
          data={options}
          renderItem={(i) => (
            <ListItem
              {...renderOption(i)}
              onClick={() => onToggle(keyExtractor(i))}
              icon={
                value.includes(keyExtractor(i)) ? "checkbox-on" : "checkbox-off"
              }
            />
          )}
        />
      </FieldDropdown>
    </FieldWrapper>
  );
}
